import { useState, useEffect } from 'react';
import { User, Package, LogOut, Edit, Save, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';

interface UserProfile {
  id: string;
  username: string;
  full_name: string | null;
  address: string | null;
  phone: string | null;
}

export default function Profile() {
  const { user, profile, setProfile, signOut } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    full_name: '',
    address: '',
    phone: '',
  });
  const [usernameError, setUsernameError] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    async function fetchProfile() {
      if (!user) return;

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
        } else {
          setProfile(data as UserProfile);
          setFormData({
            username: data?.username || '',
            full_name: data?.full_name || '',
            address: data?.address || '',
            phone: data?.phone || '',
          });
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [user, setProfile]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear username error when user types
    if (name === 'username') {
      setUsernameError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    // Validate username
    if (!formData.username.trim()) {
      setUsernameError('Lo username è obbligatorio');
      return;
    }
    
    try {
      // Check if username is already taken (but not by current user)
      // profiles è leggibile solo per il proprio profilo: la verifica passa
      // dalla RPC, che risponde solo vero/falso.
      setSaveError('');
      if (formData.username !== profile?.username) {
        const { data: usernameTaken, error: checkError } = await supabase.rpc(
          'is_username_taken',
          { p_username: formData.username },
        );

        if (checkError) {
          console.error('Error checking username:', checkError);
          setSaveError('Non è stato possibile salvare. Riprova tra qualche istante.');
          return;
        }

        if (usernameTaken) {
          setUsernameError('Questo username è già in uso. Scegline un altro.');
          return;
        }
      }
      
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          username: formData.username,
          full_name: formData.full_name,
          address: formData.address,
          phone: formData.phone,
        });

      if (error) {
        console.error('Error updating profile:', error);
        if (error.code === '23505') {
          setUsernameError('Questo username è già in uso. Scegline un altro.');
        } else {
          setSaveError('Non è stato possibile salvare. Riprova tra qualche istante.');
        }
      } else {
        setProfile({
          id: user.id,
          username: formData.username,
          full_name: formData.full_name,
          address: formData.address,
          phone: formData.phone,
        } as UserProfile);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <AuthGuard>
      <section className="container py-10">
        <h1 className="text-3xl font-bold text-foreground mb-6">
          Il mio profilo
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-card rounded-lg shadow-md p-6">
              <div className="flex flex-col items-center mb-6">
                <div className="bg-primary/10 rounded-full p-4 mb-4">
                  <User className="h-16 w-16 text-primary" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">
                  {profile?.username || user?.email?.split('@')[0] || 'Utente'}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {user?.email}
                </p>
              </div>
              
              <nav className="space-y-2">
                <button className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg bg-primary/10 text-primary">
                  <User className="h-5 w-5" />
                  <span>Profilo</span>
                </button>
                <button 
                  onClick={() => navigate('/orders')}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-foreground/80 hover:bg-muted"
                >
                  <Package className="h-5 w-5" />
                  <span>I miei ordini</span>
                </button>
                <button 
                  onClick={handleSignOut}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Esci</span>
                </button>
              </nav>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="md:col-span-2">
            <div className="bg-card rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-foreground">
                  Dati personali
                </h2>
                {!isEditing ? (
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="flex items-center space-x-1 text-primary hover:text-magenta-600"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Modifica</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => {
                      setIsEditing(false);
                      setUsernameError('');
                      setSaveError('');
                      // Reset form data to current profile
                      if (profile) {
                        setFormData({
                          username: profile.username || '',
                          full_name: profile.full_name || '',
                          address: profile.address || '',
                          phone: profile.phone || '',
                        });
                      }
                    }}
                    className="flex items-center space-x-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                    <span>Annulla</span>
                  </button>
                )}
              </div>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-foreground/80 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-2 rounded-lg border ${
                        usernameError ? 'border-red-500 dark:border-red-500' : 'border-border'
                      } bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary`}
                    />
                    {usernameError && (
                      <p className="mt-1 text-sm text-red-500">{usernameError}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-medium text-foreground/80 mb-1">
                      Nome e cognome
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground/80 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-4 py-2 rounded-lg border border-border bg-muted text-muted-foreground cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      L'email non può essere modificata
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground/80 mb-1">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-foreground/80 mb-1">
                      Indirizzo
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    className="flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors btn-hover-scale"
                  >
                    <Save className="h-5 w-5" />
                    <span>Salva modifiche</span>
                  </button>
                  {saveError && <p className="text-sm text-red-500">{saveError}</p>}
                </form>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Username
                    </h3>
                    <p className="text-foreground mt-1">
                      {profile?.username || 'Non indicato'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Nome e cognome
                    </h3>
                    <p className="text-foreground mt-1">
                      {profile?.full_name || 'Non indicato'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Email
                    </h3>
                    <p className="text-foreground mt-1">
                      {user?.email}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Telefono
                    </h3>
                    <p className="text-foreground mt-1">
                      {profile?.phone || 'Non indicato'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Indirizzo
                    </h3>
                    <p className="text-foreground mt-1">
                      {profile?.address || 'Non indicato'}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
          </div>
        </div>
      </section>
    </AuthGuard>
  );
}
